import { OperationResponse } from "api/actor";
import { ConfirmationOverlay } from "components/feedback/ConfirmationOverlay";
import { Sizes } from "components/forms/NumberSelector";
import { AddIcon } from "components/icons/Icons";
import { LightAction } from "components/ui/LightAction";
import { NsKey } from "enums/NsKey";
import { SubsidiaryForm } from "features/new-actor/components/subsidiaries/SubsidiaryForm";
import {
  mapSubsidiaryToAttachCommand,
  mapSubsidiaryToCreateCommand,
  mapSubsidiaryToUpdateCommand
} from "features/new-actor/mappers/optim/mapSubsidiaries";
import { SubsidiaryFormValues } from "features/new-actor/types/optim/subsidiaryFormValues";
import { getRegistrationNumberRefusal } from "features/new-actor/utils/subsidiariesGuards";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { usePanel } from "hooks/usePanel";
import { Network } from "lucide-react";
import { FC, useCallback, useState } from "react";
import { toast } from "react-toastify";
import { actorsAPI } from "services/swagger-api";
import { withPersonName } from "shared/utils/panelTitle";
import { runOperation } from "shared/utils/runOperation";
import { DashedAddButton } from "components/ui/DashedAddButton";
import { AddSubsidiaryPanel } from "./AddSubsidiaryPanel";
import { LegalProtectionEmptyCard } from "./LegalProtectionEmptyCard";
import { LegalProtectionSection } from "./LegalProtectionSection";
import { LegalProtectionSubsidiaryCard } from "./LegalProtectionSubsidiaryCard";
import { InheritedIdentityBlock, renderSubsidiaryPanelLayout } from "./SubsidiaryPanelFields";

interface LegalProtectionSubsidiariesSectionProps {
  /** Company of the folder. Undefined: the section carries no action. */
  legalEntityId?: string;
  /** SIRET of the company, refused as a subsidiary SIRET. */
  parentRegistrationNumber?: string | null;
  /** Subsidiaries as saved, loaded by the step page. */
  subsidiaries: SubsidiaryFormValues[];
  disabled: boolean;
  /** Reloads the step data once a subsidiary is saved or removed. */
  onSaved: () => void | Promise<void>;
}

/**
 * Subsidiaries of a legal protection folder, one white card each. Every row is saved at once by
 * its own route: nothing is kept in a form waiting for a later synchronisation.
 */
export const LegalProtectionSubsidiariesSection: FC<LegalProtectionSubsidiariesSectionProps> = ({
  legalEntityId,
  parentRegistrationNumber,
  subsidiaries,
  disabled,
  onSaved
}) => {
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.ActorForm]);
  const { openPanel, closePanel } = usePanel();
  // Subsidiary the bin was clicked on: detached only once confirmed.
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // The panel stays open on a refusal: the row is kept as typed, with the back's message.
  const runCall = useCallback(
    (call: () => Promise<OperationResponse>) => runOperation(call, t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SAVE_ERROR")),
    [t]
  );

  const submitFromPanel = useCallback(async (call: () => Promise<OperationResponse>) => {
    if (await runCall(call)) {
      closePanel();
      await onSaved();
    }
  }, [runCall, closePanel, onSaved]);

  // Same refusals as the back, checked before the call so the panel keeps the typed row.
  const isRegistrationNumberRefused = useCallback((values: SubsidiaryFormValues, currentId?: string): boolean => {
    const refusal = getRegistrationNumberRefusal(
      values.registrationNumber,
      parentRegistrationNumber,
      subsidiaries,
      currentId
    );
    if (refusal) {
      toast.error(t(`ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.ERRORS.${refusal}`));
    }
    return Boolean(refusal);
  }, [parentRegistrationNumber, subsidiaries, t]);

  const onAddSubsidiary = useCallback(() => {
    if (!legalEntityId) return;
    openPanel(
      {
        title: t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.LABELS.ADD"),
        size: Sizes["2xl"],
      },
      <AddSubsidiaryPanel
        legalEntityId={legalEntityId}
        parentRegistrationNumber={parentRegistrationNumber}
        subsidiaries={subsidiaries}
        onCreate={async (values) => {
          if (isRegistrationNumberRefused(values)) return;
          await submitFromPanel(() => actorsAPI.createSubsidiary({
            personneMoraleId: legalEntityId,
            createSubsidiaryCommandDto: mapSubsidiaryToCreateCommand(values)
          }));
        }}
        onAttach={async (values) => {
          await submitFromPanel(() => actorsAPI.attachSubsidiary({
            personneMoraleId: legalEntityId,
            attachSubsidiaryCommandDto: mapSubsidiaryToAttachCommand(values)
          }));
        }}
      />
    );
  }, [openPanel, t, legalEntityId, parentRegistrationNumber, subsidiaries, isRegistrationNumberRefused, submitFromPanel]);

  // Editing an existing row: no search, the subsidiary is already attached.
  const onUpdateSubsidiary = useCallback((subsidiary: SubsidiaryFormValues) => {
    if (!legalEntityId) return;
    openPanel(
      {
        title: withPersonName(t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.LABELS.EDIT"), subsidiary.companyName),
        size: Sizes["2xl"],
      },
      <div className="flex flex-col gap-5">
        {subsidiary.legalPersonId && (
          <InheritedIdentityBlock companyName={subsidiary.companyName} registrationNumber={subsidiary.registrationNumber} />
        )}
        <SubsidiaryForm
          inPanel
          renderLayout={renderSubsidiaryPanelLayout(Boolean(subsidiary.legalPersonId))}
          pendingSubsidiary={subsidiary}
          parentRegistrationNumber={parentRegistrationNumber}
          subsidiaries={subsidiaries}
          handleSubmit={async (values) => {
            if (isRegistrationNumberRefused(values, subsidiary.id)) return;
            await submitFromPanel(() => actorsAPI.updateSubsidiary({
              personneMoraleId: legalEntityId,
              filialeId: subsidiary.id,
              updateSubsidiaryCommandDto: mapSubsidiaryToUpdateCommand(values)
            }));
          }}
        />
      </div>
    );
  }, [openPanel, t, legalEntityId, parentRegistrationNumber, subsidiaries, isRegistrationNumberRefused, submitFromPanel]);

  const onConfirmDelete = useCallback(async () => {
    if (!legalEntityId || !pendingDeleteId) return;
    const filialeId = pendingDeleteId;
    setPendingDeleteId(null);
    if (await runCall(() => actorsAPI.detachSubsidiary({ personneMoraleId: legalEntityId, filialeId }))) {
      await onSaved();
    }
  }, [legalEntityId, pendingDeleteId, runCall, onSaved]);

  const addAction = (
    <LightAction
      icon={AddIcon}
      label={t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.LABELS.ADD")}
      onClick={onAddSubsidiary}
    />
  );

  return (
    <>
      <LegalProtectionSection
        icon={<Network className="h-8 w-8 text-primary" />}
        title={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARIES")}
        hint={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SUBSIDIARIES_HINT")}
        count={subsidiaries.length}
        disabled={disabled}
        disabledHint={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SECTION_LOCKED")}
        // A disabled section renders no action at all: greying it out does not block clicks.
        aside={subsidiaries.length > 0 ? addAction : undefined}
      >
        {subsidiaries.length === 0
          ? (
            // Empty list: the dashed tile is the add button. Locked section: nothing to add yet, the card says why.
            disabled
              ? <LegalProtectionEmptyCard text={t("SUBSCRIPTION_PROCESS.LEGAL_PROTECTION.SECTION_LOCKED")} disabled />
              : <DashedAddButton label={t("ACTOR_FORM.LEGAL_ENTITY.SUBSIDIARIES.LABELS.ADD")} onClick={onAddSubsidiary} />
          )
          : (
            <div className="grid w-full grid-cols-2 gap-5">
              {subsidiaries.map(subsidiary => (
                <LegalProtectionSubsidiaryCard
                  key={subsidiary.id}
                  subsidiary={subsidiary}
                  onEdit={onUpdateSubsidiary}
                  onDelete={setPendingDeleteId}
                />
              ))}
            </div>
          )}
      </LegalProtectionSection>

      <ConfirmationOverlay
        isOpen={pendingDeleteId !== null}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={onConfirmDelete}
        title="COMMON.DELETE_CONFIRMATION_TITLE"
        description="COMMON.DELETE_CONFIRMATION_DESCRIPTION"
        confirmLabel="COMMON.DELETE"
        variant="danger"
      />
    </>
  );
};
